import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageHeader from '../../../components/owner/PageHeader';
import GymIdMemberSearch from '../../../components/receptionist/GymIdMemberSearch';
import {
  ColumnFilterHeader,
  useColumnFilters,
} from '../../../components/admin/ColumnFilterHeader';
import { usePanelToast } from '../../../context/PanelToastContext';
import { getGymMemberId } from '../../admin/members/memberData';
import { formatCurrency } from '../receptionistData';
import {
  fetchMembersPage,
  fetchPaymentsPage,
  fetchPtSalePage,
  fetchLockerSalePage,
  fetchSpaSalePage,
  fetchOxybarSalePage,
  fetchZumbaSalePage,
  fetchYogaSalePage,
  fetchGymApparelPage,
  fetchMiscellaneousSalePage,
  fetchTrainers,
  createPayment,
  deletePayment,
} from '../../../services/receptionist/receptionistApi';
import {
  ensureWalkInMember,
  isWalkInMember,
  WALK_IN_GYM_MEMBER_ID,
  WALK_IN_PAYMENT_NAME,
} from '../../../services/receptionist/walkInMember';
import { notifyPaymentsUpdated } from '../paymentStore';
import { dedupeMembers } from '../../../utils/dedupeList';
import TablePagination from '../../../components/shared/TablePagination';
import {
  combineMethodLabels,
  defaultPaymentSplits,
  serializePaymentSplits,
} from '../../../utils/splitPaymentUtils';
import { MEMBER_SEARCH_LABEL, MEMBER_SEARCH_PLACEHOLDER } from './salePageConfig';
import SaleAmountFields from './SaleAmountFields';
import SaleDurationField from './SaleDurationField';
import SaleReceiptModal from './SaleReceiptModal';
import PayBalanceModal from './PayBalanceModal';
import RemainingClearHistory from './RemainingClearHistory';
import SaleHistoryRowActions from './SaleHistoryRowActions';
import SaleEditModal from './SaleEditModal';
import { buildSaleReceiptData, buildReceiptFromPaymentRow } from './buildSaleReceipt';
import PrintActionsHeader from '../../../components/shared/PrintActionsHeader';
import {
  formatDateFilterValue,
  saleHistoryFilterDefaults,
} from './saleHistoryFilters';
import { createSaleHistoryExporter } from './exportSaleHistory';
import { isReceptionistPanelScope, RECEPTIONIST_BRANCH_EVENT } from '../../../services/gym/branchContext';
import { pkTodayRange } from '../../../utils/pkTime';
import { useSaleWriteBranch, SaleBranchField } from '../../../hooks/useSaleWriteBranch';
import {
  SaleMethodColumnHeaders,
  SaleMethodCells,
  SALE_METHOD_COLUMN_COUNT,
} from './SaleMethodTableColumns';
import { durationOptionsForSaleSource, durationValuesForSaleSource, emptySalePeriodFields, formatDurationLabel, saleEndDate, syncSalePeriodFields } from './saleDurationUtils';
import {
  saleRowStartDate,
  saleRowEndDate,
  saleRowDuration,
  saleRowPackageName,
  saleRowTrainerName,
} from './salePeriodUtils';
import { resolveSalePayment, saleRowLeft, saleRowPaid, saleRowTotal } from './salePaymentUtils';
import '../../owner/ownerPages.css';
import '../../owner/subscriptions/subscriptions.css';
import '../members/members.css';
import '../sales.css';
import './ancillarySales.css';

function saleDate(row) {
  return String(row.date || '').slice(0, 10) || '—';
}

function todayDate() {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function emptyForm(source = '') {
  const today = todayDate();
  const durationOptions = durationOptionsForSaleSource(source);
  const durationKey = durationOptions[0]?.value || '1d';
  return {
    item: '',
    ...emptySalePeriodFields(today, durationKey),
    amount: '',
    paymentSplits: defaultPaymentSplits(),
    date: today,
    note: '',
    complimentary: false,
  };
}

const HISTORY_COL_COUNT = 9 + SALE_METHOD_COLUMN_COUNT + 3;

const LIST_RPC_FETCHERS = {
  pt_sale: fetchPtSalePage,
  locker_sale: fetchLockerSalePage,
  spa_sale: fetchSpaSalePage,
  oxybar_sale: fetchOxybarSalePage,
  zumba_sale: fetchZumbaSalePage,
  yoga_sale: fetchYogaSalePage,
  gym_apparel: fetchGymApparelPage,
  miscellaneous_sale: fetchMiscellaneousSalePage,
};

export default function AncillarySalePage({ config }) {
  const toast = usePanelToast();
  const splitsTouchedRef = useRef(false);
  const [memberHits, setMemberHits] = useState([]);
  const [sales, setSales] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const historyPageSize = 50;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState({ name: '', gymId: '', member: null });
  const {
    writeBranch,
    branches: writeBranches,
    pickedBranch,
    setPickedBranch,
    showPicker: showBranchPicker,
  } = useSaleWriteBranch(selected.member, selected.gymId);
  const [form, setForm] = useState(() => emptyForm(config.source));
  const durationOptions = durationOptionsForSaleSource(config.source);
  const durationValues = durationValuesForSaleSource(config.source);
  const [receipt, setReceipt] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [saved, setSaved] = useState(false);
  const saleSource = String(config.source || '').trim();
  const listRpcFetch = LIST_RPC_FETCHERS[saleSource];
  const usesListRpc = Boolean(listRpcFetch);
  const receptionistScope = isReceptionistPanelScope();
  const todayRange = pkTodayRange();
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const lockReceptionistDates = usesListRpc && receptionistScope;
  const listRangeStart = lockReceptionistDates ? todayRange.startDate : rangeStart;
  const listRangeEnd = lockReceptionistDates ? todayRange.endDate : rangeEnd;
  const [collected, setCollected] = useState(0);
  const [trainerOptions, setTrainerOptions] = useState([]);
  /** Bumps when receptionist branch finishes syncing — history must reload (avoids permanent 0 sales). */
  const [branchTick, setBranchTick] = useState(0);
  const {
    filters,
    updateFilter,
    openFilterKey,
    toggleFilter,
  } = useColumnFilters(saleHistoryFilterDefaults());
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilters(filters), 300);
    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    const onBranch = () => {
      setHistoryPage(1);
      setBranchTick((n) => n + 1);
    };
    window.addEventListener(RECEPTIONIST_BRANCH_EVENT, onBranch);
    return () => window.removeEventListener(RECEPTIONIST_BRANCH_EVENT, onBranch);
  }, []);

  const itemColumnLabel = config.itemIsTrainer ? 'Trainer' : (config.itemLabel || 'Item');
  const allowComplimentary = Boolean(config.allowComplimentary);
  const itemDisplay = (row) => (
    config.itemIsTrainer ? saleRowTrainerName(row) : saleRowPackageName(row)
  );

  const saleExporter = useMemo(
    () => createSaleHistoryExporter({
      title: `${config.label} Sales`,
      itemLabel: itemColumnLabel,
      slug: config.source || config.id || config.label,
      getItemValue: config.itemIsTrainer ? saleRowTrainerName : saleRowPackageName,
    }),
    [config.label, config.source, config.id, config.itemIsTrainer, itemColumnLabel],
  );

  useEffect(() => {
    if (!config?.itemIsTrainer) {
      setTrainerOptions([]);
      return undefined;
    }
    let cancelled = false;
    fetchTrainers()
      .then((rows) => {
        if (!cancelled) {
          setTrainerOptions(
            (rows || [])
              .map((t) => String(t.name || t.full_name || '').trim())
              .filter(Boolean),
          );
        }
      })
      .catch(() => {
        if (!cancelled) setTrainerOptions([]);
      });
    return () => { cancelled = true; };
  }, [config?.itemIsTrainer, config?.source]);

  const loadSales = useCallback(async () => {
    try {
      setError('');
      setLoading(true);
      const source = String(config.source || '').trim();
      const rpcFetch = LIST_RPC_FETCHERS[source];
      const result = rpcFetch
        ? await rpcFetch({
          page: historyPage,
          pageSize: historyPageSize,
          filters: {
            ...debouncedFilters,
            rangeStart: listRangeStart,
            rangeEnd: listRangeEnd,
          },
        })
        : await fetchPaymentsPage({
          page: historyPage,
          pageSize: historyPageSize,
          source,
          dateFrom: listRangeStart,
          dateTo: listRangeEnd,
          filters: debouncedFilters,
        });
      setSales(result.rows || []);
      setHistoryTotal(Number(result.total) || 0);
      setCollected(Number(result.collected) || 0);
    } catch (err) {
      setError(err.message || 'Failed to load sales');
      setSales([]);
      setHistoryTotal(0);
      setCollected(0);
    } finally {
      setLoading(false);
    }
  }, [config.source, historyPage, historyPageSize, listRangeStart, listRangeEnd, debouncedFilters, branchTick]);

  useEffect(() => {
    loadSales();
  }, [loadSales]);

  useEffect(() => {
    setHistoryPage(1);
  }, [listRangeStart, listRangeEnd, config.source, debouncedFilters]);

  // Member search-as-you-type (also while editing serial / name after select)
  useEffect(() => {
    let cancelled = false;
    const q = query.trim();
    if (!q) {
      setMemberHits(selected.member ? [selected.member] : []);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const result = await fetchMembersPage({ search: q, pageSize: 25 });
        if (!cancelled) setMemberHits(dedupeMembers(result.rows || []));
      } catch {
        if (!cancelled) setMemberHits([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, selected.member]);

  const filteredSales = sales;

  const totalCollected = useMemo(() => {
    const pagePaid = filteredSales.reduce((sum, row) => sum + saleRowPaid(row), 0);
    if (usesListRpc) return collected || pagePaid;
    return pagePaid;
  }, [filteredSales, collected, usesListRpc]);

  const historyTotalPages = Math.max(1, Math.ceil(historyTotal / historyPageSize) || 1);
  const historyFrom = historyTotal === 0 ? 0 : (historyPage - 1) * historyPageSize + 1;
  const historyTo = Math.min(historyPage * historyPageSize, historyTotal);

  /** Print/export must include every row matching date + column filters, not only the open page. */
  const handleExport = async (exporterFn) => {
    try {
      if (usesListRpc && listRpcFetch) {
        const result = await listRpcFetch({
          filters: {
            ...debouncedFilters,
            rangeStart: listRangeStart,
            rangeEnd: listRangeEnd,
          },
          forExport: true,
        });
        const rows = result.rows || [];
        exporterFn(rows.length ? rows : filteredSales);
        return;
      }
      const pageSizeExport = 500;
      const source = String(config.source || '').trim();
      const all = [];
      let pageNo = 1;
      for (;;) {
        const result = await fetchPaymentsPage({
          page: pageNo,
          pageSize: pageSizeExport,
          source,
          dateFrom: listRangeStart,
          dateTo: listRangeEnd,
          filters: debouncedFilters,
          forExport: true,
        });
        const batch = result.rows || [];
        all.push(...batch);
        if (batch.length < pageSizeExport) break;
        pageNo += 1;
        if (pageNo > 100) break;
      }
      exporterFn(all.length ? all : filteredSales);
    } catch (err) {
      toast.showError(err.message || 'Export failed');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePeriodChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => syncSalePeriodFields(prev, { [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selected.member && !selected.name) {
      toast.showError('Select a member (or enter a walk-in name via search).');
      return;
    }
    if (showBranchPicker && writeBranches.length > 0 && !writeBranch) {
      toast.showError('Select a branch for this sale.');
      return;
    }
    if (config.itemIsTrainer && !String(form.item || '').trim()) {
      toast.showError('Enter or select a trainer name.');
      return;
    }
    const complimentary = allowComplimentary && Boolean(form.complimentary);
    const amountBlank = String(form.amount ?? '').trim() === '';
    let payment = complimentary
      ? { total: 0, paid: 0, left: 0, status: 'complimentary' }
      : resolveSalePayment(amountBlank ? 0 : form.amount, form.paymentSplits);
    if (!complimentary) {
      if (!Number.isFinite(payment.total) || payment.total < 0) {
        toast.showError('Enter a valid total amount, or leave blank to set later.');
        return;
      }
      // 0 paid is allowed (pay later) — sale stays pending until edited with a payment
      if (!Number.isFinite(payment.paid) || payment.paid < 0) {
        toast.showError('Enter a valid paid amount (0 is allowed for pay later).');
        return;
      }
      // Blank total + money collected → use collected as total for now
      if (amountBlank && payment.paid > 0) {
        payment = { total: payment.paid, paid: payment.paid, left: 0, status: 'completed' };
      } else if (payment.paid > payment.total) {
        toast.showError('Total paid cannot exceed the sale total.');
        return;
      }
    }
    if (!durationValues.includes(form.duration)) {
      toast.showError('Choose a valid duration.');
      return;
    }
    try {
      setSaving(true);
      const walkInSelected = isWalkInMember(selected.member) || selected.gymId === WALK_IN_GYM_MEMBER_ID;
      const memberName = String(selected.name || selected.member?.name || '').trim()
        || (walkInSelected ? WALK_IN_PAYMENT_NAME : '');
      const gymId = String(selected.gymId || getGymMemberId(selected.member) || '').trim()
        || (walkInSelected ? WALK_IN_GYM_MEMBER_ID : '');
      const memberId = selected.member?.id || null;
      const baseItem = String(form.item || '').trim() || config.label;
      const durationLabel = formatDurationLabel(form.duration, durationOptions);
      // Keep sale type in package name so Z Report can group even if source was lost
      const saleTag = String(config.label || '').trim();
      const taggedItem = saleTag && !new RegExp(saleTag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i').test(baseItem)
        ? `${saleTag} — ${baseItem}`
        : baseItem;
      const packageLabel = taggedItem;
      const paymentSplits = complimentary ? [] : serializePaymentSplits(form.paymentSplits);
      const transactionDate = form.date || todayDate();
      const startDate = form.startDate || transactionDate;
      const endDate = form.endDate || saleEndDate(startDate, form.duration);
      const paymentId = complimentary ? '—' : `PAY-${String(Date.now()).slice(-6)}`;
      const durationNote = complimentary
        ? `Complimentary ${config.label} · Duration: ${durationLabel} (${startDate} to ${endDate})`
        : `Duration: ${durationLabel} (${startDate} to ${endDate})`;
      const paymentNote = [
        form.note || (complimentary ? `Complimentary ${config.label}` : ''),
        durationNote,
      ].filter(Boolean).join(' · ');

      const saved = await createPayment({
        id: complimentary ? undefined : paymentId,
        paymentId: complimentary ? '' : paymentId,
        memberId,
        member: memberName,
        package: packageLabel,
        amount: payment.paid,
        packagePrice: payment.total,
        method: complimentary ? 'Complimentary' : combineMethodLabels(form.paymentSplits),
        paymentSplits,
        date: transactionDate,
        status: payment.status,
        source: config.source,
        paymentNote,
        gymId,
        startDate,
        endDate,
        duration: durationLabel,
        branch: writeBranch,
      });

      const receiptData = buildSaleReceiptData({
        saleTitle: config.title,
        saleLabel: config.label,
        customer: memberName,
        gymId,
        phone: selected.member?.phone || selected.member?.mobile || '',
        item: baseItem,
        itemFieldLabel: config.itemIsTrainer ? 'Trainer' : 'Item',
        duration: form.duration,
        startDate,
        endDate,
        transactionDate,
        totalAmount: payment.total,
        paymentAmount: payment.paid,
        totalLeft: payment.left,
        paymentSplits: complimentary ? [] : form.paymentSplits,
        paymentNote: form.note || (complimentary ? `Complimentary ${config.label}` : ''),
        status: payment.status,
        invoiceId: complimentary ? '' : saved?.invoiceId,
        paymentId: complimentary ? '—' : paymentId,
        complimentary,
      });

      notifyPaymentsUpdated();
      toast.showSaved(complimentary ? `Complimentary ${config.label}` : config.label);
      setReceipt(receiptData);
      setSaved(true);
      splitsTouchedRef.current = false;
      setForm(emptyForm(config.source));
      setQuery('');
      setSelected({ name: '', gymId: '', member: null });
      await loadSales();
    } catch (err) {
      toast.showError(err.message || 'Failed to save sale');
    } finally {
      setSaving(false);
    }
  };

  const handleShowReceipt = (row) => {
    setReceipt(buildReceiptFromPaymentRow(row, {
      title: config.title,
      label: config.label,
      itemIsTrainer: Boolean(config.itemIsTrainer),
      itemFieldLabel: config.itemIsTrainer ? 'Trainer' : 'Item',
    }));
  };

  const handleBalancePaid = async (receiptData) => {
    setReceipt(receiptData);
    await loadSales();
  };

  const handleDeleteSale = async () => {
    if (!deleteTarget || deleting) return;
    const paymentDbId = deleteTarget.rawId || deleteTarget.id;
    try {
      setDeleting(true);
      await deletePayment(paymentDbId);
      setDeleteTarget(null);
      notifyPaymentsUpdated();
      toast.showDeleted(config.label);
      await loadSales();
    } catch (err) {
      toast.showError(err.message || 'Failed to delete sale');
    } finally {
      setDeleting(false);
    }
  };

  if (!config) {
    return <p className="owner-placeholder-text">Sale page not found.</p>;
  }

  return (
    <div className="rec-ancillary-sale">
      <PageHeader title={config.title} subtitle={config.subtitle}>
        {saved && receipt && (
          <span className="owner-badge owner-badge-success">
            {config.label} saved — {receipt.invoiceId}
          </span>
        )}
      </PageHeader>

      {loading && <p className="owner-placeholder-text owner-mb-16">Loading…</p>}
      {error && (
        <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>
          {error}
        </p>
      )}

      <div className="owner-card rec-panel-card rec-ancillary-sale-form-card">
        <h2 className="owner-card-title">New {config.label}</h2>
        <form className="subscription-form rec-ancillary-sale-form" onSubmit={handleSubmit}>
          <div className="rec-ancillary-sale-grid">
            <div className="owner-form-group rec-ancillary-sale-full">
              <GymIdMemberSearch
                id={`${config.id}-member`}
                label={MEMBER_SEARCH_LABEL}
                query={query}
                onQueryChange={setQuery}
                selectedName={selected.name}
                selectedGymId={selected.gymId}
                selectedMember={selected.member}
                onSelect={setSelected}
                members={memberHits}
                serverFiltered
                editableSelection
                placeholder={MEMBER_SEARCH_PLACEHOLDER}
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8, alignItems: 'center' }}>
                <button
                  type="button"
                  className="owner-btn owner-btn-secondary owner-btn-sm"
                  onClick={async () => {
                    try {
                      const walkIn = await ensureWalkInMember();
                      setQuery(WALK_IN_GYM_MEMBER_ID);
                      setSelected({
                        name: WALK_IN_PAYMENT_NAME,
                        gymId: WALK_IN_GYM_MEMBER_ID,
                        member: walkIn,
                      });
                      setMemberHits([walkIn]);
                    } catch (err) {
                      toast.showError(err.message || 'Could not load walk-in customer.');
                    }
                  }}
                >
                  Walk-In Customer
                </button>
                {selected.gymId === WALK_IN_GYM_MEMBER_ID && (
                  <span className="owner-placeholder-text" style={{ margin: 0, fontSize: 12 }}>
                    Walk-in selected (Serial {WALK_IN_GYM_MEMBER_ID})
                  </span>
                )}
              </div>
              {showBranchPicker && (
                <SaleBranchField
                  id={`${config.id}-branch`}
                  branches={writeBranches}
                  value={pickedBranch}
                  onChange={setPickedBranch}
                />
              )}
            </div>

            <div className="owner-form-group rec-ancillary-sale-full">
              <label className="owner-form-label" htmlFor={`${config.id}-item`}>
                {config.itemLabel}
              </label>
              <input
                id={`${config.id}-item`}
                name="item"
                className="owner-form-input"
                value={form.item}
                onChange={handleChange}
                placeholder={config.itemPlaceholder}
                list={config.itemIsTrainer ? `${config.id}-trainer-list` : undefined}
                autoComplete={config.itemIsTrainer ? 'off' : undefined}
                required={Boolean(config.itemIsTrainer)}
              />
              {config.itemIsTrainer ? (
                <datalist id={`${config.id}-trainer-list`}>
                  {trainerOptions.map((name) => (
                    <option key={name} value={name} />
                  ))}
                </datalist>
              ) : null}
            </div>

            <SaleDurationField
              idPrefix={config.id}
              duration={form.duration}
              startDate={form.startDate}
              endDate={form.endDate}
              onPeriodChange={handlePeriodChange}
              durationOptions={durationOptions}
            />

            {allowComplimentary && (
              <div className="owner-form-group rec-ancillary-sale-full">
                <label className="rec-sub-summary-check rec-add-package-tbc-check">
                  <input
                    type="checkbox"
                    checked={Boolean(form.complimentary)}
                    onChange={(e) => setForm((prev) => ({
                      ...prev,
                      complimentary: e.target.checked,
                    }))}
                  />
                  <span>Complimentary — no payment required for this customer</span>
                </label>
              </div>
            )}

            {allowComplimentary && form.complimentary ? (
              <p className="owner-placeholder-text rec-ancillary-sale-full" style={{ margin: 0 }}>
                Complimentary {config.label} — no payment will be recorded.
              </p>
            ) : (
              <SaleAmountFields
                idPrefix={config.id}
                totalAmount={form.amount}
                paymentSplits={form.paymentSplits}
                onTotalChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
                onPaymentSplitsChange={(paymentSplits) => setForm((prev) => ({ ...prev, paymentSplits }))}
                onSplitsTouched={() => { splitsTouchedRef.current = true; }}
              />
            )}

            <div className="owner-form-group">
              <label className="owner-form-label" htmlFor={`${config.id}-date`}>Date</label>
              <input
                id={`${config.id}-date`}
                name="date"
                type="date"
                className="owner-form-input"
                value={form.date}
                onChange={handleChange}
                required
              />
              <p className="owner-placeholder-text rec-sale-payment-hint">
                {allowComplimentary && form.complimentary
                  ? 'Sale / transaction date.'
                  : 'Payment / transaction date.'}
              </p>
            </div>

            <div className="owner-form-group rec-ancillary-sale-full">
              <label className="owner-form-label" htmlFor={`${config.id}-note`}>Note</label>
              <input
                id={`${config.id}-note`}
                name="note"
                className="owner-form-input"
                value={form.note}
                onChange={handleChange}
                placeholder="Optional note"
              />
            </div>
          </div>

          <div className="subscription-form-actions">
            <button type="submit" className="owner-btn owner-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : `Save ${config.label}`}
            </button>
          </div>
        </form>
      </div>

      <div className="owner-card rec-panel-card rec-member-list-card">
        <div className="rec-ancillary-sale-history-header">
          <div>
            <h2 className="owner-card-title">
              {config.label} history
            </h2>
            <p className="owner-placeholder-text" style={{ margin: '6px 0 0' }}>
              {historyTotal} sales
              {historyTotal > 0 ? ` · showing ${historyFrom}–${historyTo}` : ''}
              {filteredSales.length ? ` · ${usesListRpc ? '' : 'page collected '}${formatCurrency(totalCollected)}` : ''}
              {(listRangeStart || listRangeEnd) ? ` · ${listRangeStart || '…'} → ${listRangeEnd || '…'}` : ''}
            </p>
          </div>
        </div>
        <PrintActionsHeader
          className="print-actions-header--card"
          startDate={listRangeStart}
          endDate={listRangeEnd}
          onStartDateChange={setRangeStart}
          onEndDateChange={setRangeEnd}
          onClearDates={lockReceptionistDates ? undefined : () => { setRangeStart(''); setRangeEnd(''); }}
          datesReadOnly={lockReceptionistDates}
          onPrint={() => handleExport(saleExporter.print)}
          onCsv={() => handleExport(saleExporter.csv)}
          onPdf={() => handleExport(saleExporter.pdf)}
          onExcel={() => handleExport(saleExporter.excel)}
          onExport={() => handleExport(saleExporter.exportAll)}
          ariaLabel={`${config.label} print and export`}
        />
        {lockReceptionistDates ? (
          <p className="owner-placeholder-text owner-mb-16">
            Pakistan time · today only — {String(config.label || 'sales').toLowerCase()} sold today
          </p>
        ) : null}
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Date"
                  filterKey="date"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="Serial No"
                  filterKey="gymId"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search serial..."
                />
                <ColumnFilterHeader
                  label="Member"
                  filterKey="member"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search member..."
                />
                <ColumnFilterHeader
                  label={itemColumnLabel}
                  filterKey="item"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder={config.itemIsTrainer ? 'Search trainer...' : 'Search item...'}
                />
                <ColumnFilterHeader
                  label="Duration"
                  filterKey="duration"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search duration..."
                />
                <SaleMethodColumnHeaders
                  sales={sales}
                  filters={filters}
                  openFilterKey={openFilterKey}
                  toggleFilter={toggleFilter}
                  updateFilter={updateFilter}
                />
                <ColumnFilterHeader
                  label="Total"
                  filterKey="total"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search total..."
                />
                <ColumnFilterHeader
                  label="Total paid"
                  filterKey="totalPaid"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search paid..."
                />
                <ColumnFilterHeader
                  label="Total left"
                  filterKey="totalLeft"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search left..."
                />
                <ColumnFilterHeader
                  label="Start date"
                  filterKey="startDate"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="End date"
                  filterKey="endDate"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="Note"
                  filterKey="note"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search note..."
                />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSales.length ? filteredSales.map((row) => (
                <tr key={row.rawId || row.id}>
                  <td>{saleDate(row)}</td>
                  <td className="rec-member-gym-id">{row.gymId || '—'}</td>
                  <td>{row.member || '—'}</td>
                  <td>{itemDisplay(row)}</td>
                  <td>{saleRowDuration(row)}</td>
                  <SaleMethodCells row={row} />
                  <td>{formatCurrency(saleRowTotal(row))}</td>
                  <td className="finance-amount-positive">{formatCurrency(saleRowPaid(row))}</td>
                  <td className={saleRowLeft(row) > 0 ? 'finance-amount-negative' : ''}>
                    {formatCurrency(saleRowLeft(row))}
                  </td>
                  <td>{saleRowStartDate(row)}</td>
                  <td>{saleRowEndDate(row)}</td>
                  <td>{row.paymentNote || '—'}</td>
                  <td>
                    <SaleHistoryRowActions
                      row={row}
                      onReceipt={handleShowReceipt}
                      onPayBalance={setPayTarget}
                      onEdit={setEditTarget}
                      onDelete={setDeleteTarget}
                    />
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={HISTORY_COL_COUNT} className="owner-placeholder-text">
                    No {config.label.toLowerCase()} records yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <TablePagination
          page={historyPage}
          totalPages={historyTotalPages}
          total={historyTotal}
          from={historyFrom}
          to={historyTo}
          onPageChange={setHistoryPage}
          onPrev={() => setHistoryPage((p) => Math.max(1, p - 1))}
          onNext={() => setHistoryPage((p) => Math.min(historyTotalPages, p + 1))}
        />
      </div>

      <RemainingClearHistory title={`${config.label} — Remaining Clear`} />

      <SaleReceiptModal receipt={receipt} onClose={() => setReceipt(null)} />
      {editTarget && (
        <SaleEditModal
          key={editTarget.rawId || editTarget.id}
          row={editTarget}
          title={`Edit ${config.label}`}
          itemLabel={config.itemLabel || 'Item / Package'}
          itemPlaceholder={config.itemPlaceholder || 'Package or item name'}
          saleLabel={config.label}
          itemIsTrainer={Boolean(config.itemIsTrainer)}
          trainerOptions={trainerOptions}
          onClose={() => setEditTarget(null)}
          onSaved={loadSales}
        />
      )}
      {payTarget && (
        <PayBalanceModal
          key={payTarget.rawId || payTarget.id}
          paymentRow={payTarget}
          saleMeta={{
            title: config.title,
            label: config.label,
            itemIsTrainer: Boolean(config.itemIsTrainer),
            itemFieldLabel: config.itemIsTrainer ? 'Trainer' : 'Item',
          }}
          onClose={() => setPayTarget(null)}
          onPaid={handleBalancePaid}
        />
      )}
      {deleteTarget && (
        <div className="subscription-modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="subscription-modal admin-form-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Delete {config.label}</h2>
              <button type="button" className="subscription-modal-close" onClick={() => setDeleteTarget(null)} aria-label="Close">×</button>
            </div>
            <p className="owner-placeholder-text" style={{ margin: '0 0 16px' }}>
              Delete sale for <strong>{deleteTarget.member || 'this customer'}</strong>? This action cannot be undone.
            </p>
            <div className="subscription-form-actions">
              <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</button>
              <button type="button" className="owner-btn rec-member-btn-delete" onClick={handleDeleteSale} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
